import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CaretLeft, Lock, CheckCircle, Warning } from '@phosphor-icons/react';
import { PillInput, PillButton } from '../components/PillKit';
import { authExtrasAPI, formatApiError } from '../services/api';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const pwdOk = newPwd.length >= 8 && /[A-Za-z]/.test(newPwd) && /[0-9]/.test(newPwd);
  const match = newPwd && newPwd === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwdOk) { toast.error('Password must be 8+ characters with letters and numbers'); return; }
    if (!match) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authExtrasAPI.resetPassword(token, newPwd);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="reset-password-page">
      <header className="px-5 pt-6 pb-2 flex items-center justify-between">
        <Link to="/login" className="text-white p-1" aria-label="Back" data-testid="reset-back-btn">
          <CaretLeft className="w-6 h-6" />
        </Link>
        <Link to="/"><img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" /></Link>
        <span className="w-6" />
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-6 pt-8 pb-12">
        {!token ? (
          <div className="text-center" data-testid="reset-missing-token-panel">
            <div className="w-20 h-20 mx-auto rounded-full bg-red-500/15 border-[3px] border-red-500 flex items-center justify-center mb-6">
              <Warning className="w-10 h-10 text-red-500" weight="fill" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Missing reset token
            </h1>
            <p className="text-white/60 text-sm mb-8">This link is missing its token. Request a new password reset email.</p>
            <Link to="/forgot-password"><PillButton variant="solid" type="button" data-testid="reset-request-new-btn">Request new link</PillButton></Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done-panel">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#22C55E]/15 border-[3px] border-[#22C55E] flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-[#22C55E]" weight="fill" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Password updated
            </h1>
            <p className="text-white/60 text-sm mb-8">You&apos;re all set. Redirecting you to sign in…</p>
            <Link to="/login"><PillButton variant="solid" type="button" data-testid="reset-signin-btn">Sign in now</PillButton></Link>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-white/5 border-[3px] border-white flex items-center justify-center mb-5">
                <Lock className="w-8 h-8 text-[#22C55E]" weight="duotone" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold leading-none tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                New password
              </h1>
              <p className="text-[#22C55E] text-base sm:text-lg font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Choose something strong
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <PillInput
                id="new-pwd"
                type="password"
                required
                autoFocus
                minLength={8}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="New password"
                data-testid="reset-new-password-input"
              />
              <PillInput
                id="confirm-pwd"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm password"
                data-testid="reset-confirm-password-input"
              />

              <div className="space-y-1.5 px-2 pt-1">
                <p className={`flex items-center gap-2 text-xs ${pwdOk ? 'text-[#22C55E]' : 'text-white/40'}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pwdOk ? '#22C55E' : '#444' }} />
                  8+ characters with letters &amp; numbers
                </p>
                <p className={`flex items-center gap-2 text-xs ${match ? 'text-[#22C55E]' : 'text-white/40'}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: match ? '#22C55E' : '#444' }} />
                  Passwords match
                </p>
              </div>

              <PillButton
                type="submit"
                variant="solid"
                disabled={loading || !pwdOk || !match}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Saving…' : 'Update password'}
              </PillButton>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
